import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Shield, Plus, Clock, Key } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { CreatePrivilegeRuleForm } from "./CreatePrivilegeRuleForm";

export function PrivilegeRules() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: rules, isLoading } = useQuery({
    queryKey: ["privilegeRules"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("privilege_elevation_rules")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl">Privilege Elevation Rules</CardTitle>
            <CardDescription>
              Manage access rules and permissions for privileged operations
            </CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Create Rule
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Privilege Rule</DialogTitle>
              </DialogHeader>
              <CreatePrivilegeRuleForm onSuccess={() => setIsDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {isLoading ? (
            <p>Loading rules...</p>
          ) : rules?.length === 0 ? (
            <p>No rules found. Create your first rule to get started.</p>
          ) : (
            rules?.map((rule) => (
              <Card key={rule.id}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    {rule.name}
                  </CardTitle>
                  <CardDescription>{rule.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Duration: {rule.duration_minutes} minutes
                    </div>
                    <div className="flex items-center gap-2">
                      <Key className="h-4 w-4" />
                      {rule.requires_approval
                        ? "Requires approval"
                        : "Auto-approved"}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}